import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import {
  Trophy,
  ShieldCheck,
  Coins,
  Sparkles,
  Dices,
  Loader2,
  Wallet,
  Clock,
  Ticket,
  CheckCircle2,
  Gift,
} from 'lucide-react';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import { useBuyTicket } from '../hooks/useBuyTicket';
import { GiftTicketModal } from './GiftTicketModal';
import { PrmdAccrual } from './PrmdAccrual';
import {
  CONTRACT_ADDRESS,
  CONTRACT_PARAMS,
  IS_CONTRACT_CONFIGURED,
  TON_NETWORK,
  explorerAddress,
  explorerTx,
} from '../ton/config';
import { ExplorerLink } from './ExplorerLink';
import { WalletErrorNotice } from './WalletErrorNotice';
import { useLastWinTx } from '../hooks/useLastWinTx';
import { formatDateTime, formatInt, formatTon } from '../lib/format';
import { ticketNoun } from '../i18n/languages';

interface LotteryProps {
  state: PyramidaUiState;
}

// Safety cap: stop showing the "waiting" status if the indexed data hasn't
// reflected the purchase within this window (normally it catches up in ~15s).
// The banner is data-driven — it clears as soon as the wallet's round stake
// rises to the expected post-buy value, not after a fixed timer.
const BUY_WAIT_MAX_MS = 180_000;
// How long to keep the success confirmation visible once data has caught up.
const BUY_SUCCESS_MS = 6_000;

/** A `now` value that ticks every second, for time-window calculations. */
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function useCountdown(targetUnixSeconds: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    const diff = Math.max(0, targetUnixSeconds * 1000 - now);
    const totalSeconds = Math.floor(diff / 1000);
    return {
      ended: diff === 0,
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  }, [targetUnixSeconds, now]);
}

const pad = (n: number) => String(n).padStart(2, '0');

// Compact percentage: one decimal below 10%, whole number above (e.g. "3.4%", "42%").
function formatPct(pct: number): string {
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10}%`;
}

// Keep only digits and a single decimal separator; accept both '.' and ','.
function sanitizeQty(raw: string): string {
  const cleaned = raw.replace(/[^0-9.,]/g, '');
  const sepIndex = cleaned.search(/[.,]/);
  if (sepIndex === -1) return cleaned;
  const head = cleaned.slice(0, sepIndex + 1);
  const tail = cleaned.slice(sepIndex + 1).replace(/[.,]/g, '');
  return head + tail;
}

// Parse the user-entered quantity, treating ',' as the decimal point.
function parseQty(raw: string): number {
  return parseFloat(raw.replace(',', '.'));
}

export function Lottery({ state }: LotteryProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const { buyTicket, pending, error, lastTxAt } = useBuyTicket();
  // Deep-link the "last winner" card to the prize-payout transaction.
  const lastWinTxHash = useLastWinTx(state.lottery.lastLotteryTime);
  // Tracks an in-flight purchase so we can keep the user informed until the
  // indexed round stake actually reflects it (and then confirm success).
  const [pendingBuy, setPendingBuy] = useState<{ expectedStake: number; sentAt: number } | null>(
    null,
  );
  // Snapshot of {amount, baseline stake} captured at click time, read by the
  // lastTxAt effect without making `myStakeTon` an effect dependency.
  const buyContextRef = useRef<{ amount: number; baseline: number } | null>(null);
  const [qtyInput, setQtyInput] = useState('1');
  const [giftOpen, setGiftOpen] = useState(false);
  const { ended, days, hours, minutes, seconds } = useCountdown(state.lottery.roundEndTime);
  const now = useNow();
  const { lottery } = state;
  const me = state.me;

  // This wallet's participation in the live round (ticket weight = TON staked).
  const myStakeTon = me?.roundStakeTon ?? 0;
  const myTickets = lottery.ticketPrice > 0 ? myStakeTon / lottery.ticketPrice : 0;
  const roundTickets = lottery.ticketPrice > 0 ? lottery.roundStakeTon / lottery.ticketPrice : 0;
  const mySharePct =
    lottery.roundStakeTon > 0 ? (myStakeTon / lottery.roundStakeTon) * 100 : 0;

  // First paint before the first successful read: show skeletons, not zeros.
  const firstLoad = IS_CONTRACT_CONFIGURED && state.lastUpdated === null;

  // After a buy is sent, watch the wallet's round stake until it rises to the
  // expected post-buy value; only then is the purchase visible in the stats.
  const dataCaughtUp =
    pendingBuy !== null &&
    (me?.roundStakeKnown ?? false) &&
    myStakeTon + 1e-9 >= pendingBuy.expectedStake;
  const waitTimedOut = pendingBuy !== null && now - pendingBuy.sentAt > BUY_WAIT_MAX_MS;
  // Keep the reassuring "waiting" status from the moment the tx is sent until
  // the indexed data confirms it (or the safety cap elapses).
  const awaitingConfirmation = pendingBuy !== null && !dataCaughtUp && !waitTimedOut;
  const justConfirmed = pendingBuy !== null && dataCaughtUp;

  // When a buy is accepted by the wallet (lastTxAt advances), start tracking it
  // against the expected post-buy stake captured at click time.
  useEffect(() => {
    if (lastTxAt === null) return;
    const ctx = buyContextRef.current;
    if (!ctx) return;
    setPendingBuy({ expectedStake: ctx.baseline + ctx.amount, sentAt: lastTxAt });
  }, [lastTxAt]);

  // Clear the tracker once data has caught up (after a brief success window) or
  // the safety cap elapses.
  useEffect(() => {
    if (pendingBuy === null) return;
    if (dataCaughtUp) {
      const id = setTimeout(() => setPendingBuy(null), BUY_SUCCESS_MS);
      return () => clearTimeout(id);
    }
    if (waitTimedOut) setPendingBuy(null);
  }, [pendingBuy, dataCaughtUp, waitTimedOut]);

  // Switching wallets must not carry over a previous wallet's pending state.
  useEffect(() => {
    setPendingBuy(null);
    buyContextRef.current = null;
  }, [address]);

  // Ticket quantity may be fractional (e.g. 1,05). The on-chain weight equals
  // the GRAM amount sent, so total = qty * ticketPrice, with a 1 GRAM minimum.
  const ticketPrice = lottery.ticketPrice;
  const minQty = CONTRACT_PARAMS.minEntryTon / ticketPrice;
  const qty = parseQty(qtyInput);
  const totalTon = Number.isFinite(qty) ? Math.round(qty * ticketPrice * 1e9) / 1e9 : NaN;
  const qtyValid = Number.isFinite(qty) && totalTon >= CONTRACT_PARAMS.minEntryTon - 1e-9;

  const stepQty = (delta: number) => {
    const base = Number.isFinite(qty) ? qty : minQty;
    const next = Math.max(minQty, Math.round((base + delta) * 100) / 100);
    setQtyInput(String(next).replace('.', ','));
  };

  const buyLabel = !qtyValid
    ? t('lottery.buyMin', { amount: formatTon(CONTRACT_PARAMS.minEntryTon) })
    : t('lottery.buy', {
        qty: formatTon(qty),
        word: ticketNoun(lang, qty),
        total: formatTon(totalTon),
      });

  const statValue = (node: ReactNode) =>
    firstLoad ? <span className="skeleton skeleton--value" /> : node;

  return (
    <section className="content-section lottery">
      <header className="page-head">
        <h1 className="lottery-title">{t('lottery.title')}</h1>
        <span className="page-head__sub">{t('lottery.subtitle')}</span>
      </header>

      {lottery.isDrawing && (
        <div className="banner banner--active">
          <Dices size={18} aria-hidden="true" />
          {t('lottery.drawing')}
        </div>
      )}
      {!lottery.isDrawing && ended && !firstLoad && (
        <div className="banner banner--active banner--ripe">
          <Dices size={18} aria-hidden="true" />
          {t('lottery.drawReady')}
        </div>
      )}

      <div className="lottery-grid">
        <div className="lottery-grid__main">
          <div className={`countdown${ended && !firstLoad ? ' countdown--ripe' : ''}`}>
            <div className="countdown__label">
              {ended ? t('lottery.roundEnded') : t('lottery.untilRoundEnd')}
            </div>
            <div className="countdown__timer">
              {days > 0 && <span>{pad(days)}{t('lottery.dayShort')} </span>}
              {pad(hours)}:{pad(minutes)}:{pad(seconds)}
            </div>
            <div className="countdown__sub">
              <Clock size={13} aria-hidden="true" />{' '}
              {t('lottery.endsAt', { date: formatDateTime(lottery.roundEndTime) })}
            </div>
          </div>

          <div className="prize-card">
            <div className="prize-card__label">
              {t('lottery.roundPrizeLabel', { pct: CONTRACT_PARAMS.lotteryWinnerShare })}
            </div>
            <div className="prize-card__value">
              {firstLoad ? (
                <span className="skeleton skeleton--hero" />
              ) : (
                `${formatTon(lottery.projectedPrize)} GRAM`
              )}
            </div>
            <div className="prize-card__split">
              <span>
                <Coins size={15} aria-hidden="true" />{' '}
                {t('lottery.prizePool')}:{' '}
                <b>{formatTon(lottery.prizePool)} GRAM</b>
              </span>
            </div>
          </div>

          <PrmdAccrual token={state.token} variant="lottery" />

          {IS_CONTRACT_CONFIGURED && (
            <div className="ticket-qty">
              <span className="ticket-qty__label">{t('lottery.ticketCount')}</span>
              <div className="ticket-qty__control">
                <button
                  type="button"
                  className="ticket-qty__step"
                  onClick={() => stepQty(-1)}
                  disabled={pending || (Number.isFinite(qty) && qty <= minQty)}
                  aria-label={t('lottery.decrease')}
                >
                  −
                </button>
                <input
                  className="ticket-qty__input"
                  type="text"
                  inputMode="decimal"
                  value={qtyInput}
                  onChange={(e) => setQtyInput(sanitizeQty(e.target.value))}
                  disabled={pending}
                  aria-label={t('lottery.ticketCount')}
                />
                <button
                  type="button"
                  className="ticket-qty__step"
                  onClick={() => stepQty(1)}
                  disabled={pending}
                  aria-label={t('lottery.increase')}
                >
                  +
                </button>
              </div>
            </div>
          )}

          <div className="cta-block">
            <button
              type="button"
              className="buy-ticket-btn"
              disabled={pending || !IS_CONTRACT_CONFIGURED || (!!address && !qtyValid)}
              onClick={() => {
                if (!address) {
                  tonConnectUI.openModal();
                  return;
                }
                buyContextRef.current = { amount: totalTon, baseline: myStakeTon };
                void buyTicket(null, totalTon);
              }}
            >
              {pending ? (
                <>
                  <Loader2 className="spin" size={18} aria-hidden="true" /> {t('lottery.sending')}
                </>
              ) : !address ? (
                <>
                  <Wallet size={18} aria-hidden="true" /> {t('lottery.connectWallet')}
                </>
              ) : (
                buyLabel
              )}
            </button>

            {address && IS_CONTRACT_CONFIGURED && (
              <button
                type="button"
                className="gift-ticket-btn"
                onClick={() => setGiftOpen(true)}
              >
                <Gift size={16} aria-hidden="true" /> {t('lottery.giftButton')}
              </button>
            )}

            <ul className="trust-row">
              {IS_CONTRACT_CONFIGURED && (
                <li>
                  <a
                    className="trust-chip"
                    href={explorerAddress(CONTRACT_ADDRESS)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ShieldCheck size={14} aria-hidden="true" />{' '}
                    {t('lottery.contractChip', { network: TON_NETWORK })}
                  </a>
                </li>
              )}
              <li className="trust-chip">
                <Coins size={14} aria-hidden="true" />{' '}
                {t('lottery.minChip', { amount: formatTon(CONTRACT_PARAMS.minEntryTon) })}
              </li>
              <li className="trust-chip">
                <Sparkles size={14} aria-hidden="true" /> {t('lottery.payoutOnchain')}
              </li>
            </ul>
          </div>

          {!IS_CONTRACT_CONFIGURED && (
            <p className="hint hint--warn">{t('lottery.notConfigured')}</p>
          )}
          {/* Transient read/connection failures are no longer shown as an
              on-page error banner (stale data simply stays until the next poll
              succeeds). The buy-flow error below is a deliberate, action-scoped
              notification and is kept. */}
          {error && <WalletErrorNotice message={error} />}
          {pending && (
            <div className="banner banner--active">
              <Loader2 className="spin" size={18} aria-hidden="true" /> {t('lottery.confirmInWallet')}
            </div>
          )}
          {!pending && awaitingConfirmation && (
            <div className="banner banner--active">
              <Loader2 className="spin" size={18} aria-hidden="true" /> {t('lottery.buySent')}
            </div>
          )}
          {!pending && justConfirmed && (
            <div className="banner banner--success">
              <CheckCircle2 size={18} aria-hidden="true" /> {t('lottery.bought')}
            </div>
          )}
        </div>

        <div className="lottery-grid__side">
          {address && me?.registered && (
            <div className="stat-card stat-card--accent">
              <div className="stat-card__label">
                <Ticket size={13} aria-hidden="true" /> {t('lottery.myStake')}
              </div>
              <div className="stat-card__value">
                {statValue(
                  myStakeTon > 0
                    ? `${formatTon(myTickets)} ${ticketNoun(lang, myTickets)}`
                    : `0 ${ticketNoun(lang, 0)}`,
                )}
              </div>
              <div className="stat-card__sub">
                {!me.roundStakeKnown
                  ? t('lottery.ticketsUnavailable')
                  : myStakeTon > 0
                    ? t('lottery.myShareSub', {
                        tickets: formatTon(myTickets),
                        word: ticketNoun(lang, myTickets),
                        pct: formatPct(mySharePct),
                      })
                    : t('lottery.noTicketsYet')}
              </div>
            </div>
          )}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card__label">{t('lottery.roundStake')}</div>
              <div className="stat-card__value">
                {statValue(`${formatTon(roundTickets)} ${ticketNoun(lang, roundTickets)}`)}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">{t('lottery.participants')}</div>
              <div className="stat-card__value">{statValue(formatInt(lottery.participantsCount))}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">{t('lottery.totalWinners')}</div>
              <div className="stat-card__value">{statValue(formatInt(lottery.totalWinners))}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">{t('lottery.totalPaid')}</div>
              <div className="stat-card__value">
                {statValue(`${formatTon(lottery.totalWinsAmount)} GRAM`)}
              </div>
            </div>
          </div>

          {lottery.lastWinnerAddress && (
            <div className="winner-card">
              <Trophy className="winner-card__crown" size={30} aria-hidden="true" />
              <div className="winner-card__body">
                <div className="stat-card__label">{t('lottery.lastWinner')}</div>
                <div className="winner-card__amount">{formatTon(lottery.lastWinAmount)} GRAM</div>
                <ExplorerLink
                  address={lottery.lastWinnerAddress}
                  href={lastWinTxHash ? explorerTx(lastWinTxHash) : undefined}
                />
                <div className="winner-card__date">{formatDateTime(lottery.lastLotteryTime)}</div>
              </div>
            </div>
          )}

          {!state.isLive && (
            <p className="hint">{t('lottery.demoHint')}</p>
          )}
        </div>
      </div>

      {giftOpen && (
        <GiftTicketModal
          ticketPrice={ticketPrice}
          onClose={() => setGiftOpen(false)}
        />
      )}
    </section>
  );
}
